import { space, SpaceBetween, tok } from '@sunwebgroup/ui-components'

import { Typography } from '@sunwebgroup/ui-components/uds'
import { isEmpty } from 'ramda'
import * as React from 'react'

import { styled } from 'styled-components'
import { useIntl } from '@/intl'
import { useOptimizelyFlagContext } from '@/scopes/common/infrastructure/ab-testing/optimizely/useOptimizely'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { routesSelector } from '@/scopes/prices/components/selectors'
import { CollapseByHeight } from '@/scopes/prices/components/TransportSelector/CollapseByHeight'
import { Column } from '@/scopes/prices/components/TransportSelector/Column'
import { FlightCard } from '@/scopes/prices/components/TransportSelector/FlightSelector/FlightCard'
import { FlightCardNew } from '@/scopes/prices/components/TransportSelector/FlightSelector/FlightSelectorNew/FlightCardNew'
import { ArrivalTitle, DepartureTitle } from '@/scopes/prices/components/TransportSelector/FlightSelector/Headers'
import { createFlightComponentInteractionEvent } from '@/scopes/prices/domain/analytics'
import { PricesActionsContext } from '@/scopes/prices/domain/PricesActionsContext'
import { usePricesSelector } from '@/scopes/prices/infrastructure/usePricesSelector'

const Wrapper = styled.div`
  display: flex;
  width: 100%;

  ${Column}:first-child {
    margin-right: ${space('primitives_space_16')};
  }
`

const FlightSelectorTitle = styled(Typography)`
  display: block;
`

const FlightSelectorContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space('primitives_space_24')};
  padding: ${space('primitives_space_24')};
  border: 2px solid ${tok('primitives_color_neutral_100')};
  border-radius: 12px;
`
export function FlightSelectorDesktop() {
  const { inboundRoutes, outboundRoutes } = usePricesSelector(routesSelector)

  if (isEmpty(outboundRoutes) || isEmpty(inboundRoutes))
    return null

  return (
    <>
      <FlightSelector />
    </>
  )
}

function FlightSelector() {
  const { enableAlternateFlightVisualisation } = useOptimizelyFlagContext()
  const Card = enableAlternateFlightVisualisation ? FlightCardNew : FlightCard
  const { setInboundRouteId, setOutboundRouteId } = React.use(PricesActionsContext)
  const intl = useIntl()
  const { inboundRoutes, outboundRoutes, selectedOutboundRouteId, selectedInboundRouteId }
    = usePricesSelector(routesSelector)
  const { sendEvent } = useAnalytics()

  function handleCollapseToggle(result: string) {
    sendEvent(createFlightComponentInteractionEvent({ type: 'outbound', result }))
  }

  const content = (
    <Wrapper id="FlightSelector">
      <Column value={selectedOutboundRouteId as string}>
        <SpaceBetween direction="y" spacing={space('primitives_space_16')}>
          <DepartureTitle />
          {outboundRoutes.map((item, index) => (
            <Card
              key={index}
              {...item}
              setSelected={setOutboundRouteId}
              isSelected={selectedOutboundRouteId === item.id}
            />
          ))}
        </SpaceBetween>
      </Column>
      <Column value={selectedInboundRouteId as string}>
        <SpaceBetween direction="y" spacing={space('primitives_space_16')}>
          <ArrivalTitle />
          {inboundRoutes.map((item, index) => (
            <Card
              key={index}
              {...item}
              setSelected={setInboundRouteId}
              isSelected={selectedInboundRouteId === item.id}
            />
          ))}
        </SpaceBetween>
      </Column>
    </Wrapper>
  )
  return (
    <FlightSelectorContainer>
      <FlightSelectorTitle variant="display-f-regular">
        {intl.formatMessage({ id: 'prices_transport_selector_flight-selector_title' })}
      </FlightSelectorTitle>

      {outboundRoutes.length < 3
        ? (
            <>{content}</>
          )
        : (
            <CollapseByHeight
              openText={intl.formatMessage({ id: 'prices_transport_selector_flight-selector-show-less' })}
              closeText={intl.formatMessage({ id: 'prices_transport_selector_flight-selector-show-more' })}
              height="370px"
              onClick={handleCollapseToggle}
            >
              {content}
            </CollapseByHeight>
          )}
    </FlightSelectorContainer>
  )
}
